import { tr } from "./language";
// Logo variants — import as local assets (original = coloured, for light backgrounds)
import vooclyLogo         from "../assets/voocly-logo.svg";
import vooclyLogoWhite    from "../assets/voocly-logo-white.svg";
import vooclyLogoBlack    from "../assets/voocly-logo-black.svg";
import vooclyIcon         from "../assets/voocly-icon.svg";
import vooclyIconWhite    from "../assets/voocly-icon-white.svg";
import vooclyIconBlack    from "../assets/voocly-icon-black.svg";

export const SUPPLIER_LOGO       = vooclyLogo;        // coloured — header, footer, auth pages (light bg)
export const SUPPLIER_LOGO_WHITE = vooclyLogoWhite;   // white — dark/image backgrounds
export const SUPPLIER_LOGO_BLACK = vooclyLogoBlack;   // black — print / high-contrast
export const SUPPLIER_ICON       = vooclyIcon;        // coloured icon only (no text)
export const SUPPLIER_ICON_WHITE = vooclyIconWhite;
export const SUPPLIER_ICON_BLACK = vooclyIconBlack;

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Password must be at least 8 chars and contain at least one digit
export const PASSWORD_MIN_LENGTH = 8;
export const validatePassword = (pw) => {
  if (!pw) return tr("Password is required.");
  if (pw.length < 8) return tr("Password must be at least 8 characters long.");
  if (!/\d/.test(pw)) return tr("Password must contain at least 1 number.");
  return "";
};

export const LANGUAGES = [
  {
    group: "Languages",
    items: [
      { code: "en", label: "English", region: "United States" },
      { code: "es", label: "Español", region: "Spain" },
      { code: "zh", label: "中文", region: "China" },
      { code: "fr", label: "Français", region: "France" },
      { code: "it", label: "Italiano", region: "Italy" },
      { code: "de", label: "Deutsch", region: "Deutschland" },
      { code: "ar", label: "العربية", region: "العالم العربي" },
    ],
  },
];

export const BUSINESS_TYPES = [
  {
    id: "registered-company",
    get label() { return tr("Registered company"); },
    get description() { return tr("Legally incorporated companies operating under formal business registration."); },
    icon: "store",
  },
  {
    id: "certified-individual",
    get label() { return tr("Certified individual"); },
    get description() { return tr("Sole proprietors or freelancers officially registered for business activities."); },
    icon: "user-star",
  },
  {
    id: "uncertified-individual",
    get label() { return tr("Uncertified individual"); },
    get description() { return tr("Individuals conducting business without formal registration or licenses."); },
    icon: "user",
  },
  {
    id: "non-profit",
    get label() { return tr("Non-profit organization"); },
    get description() { return tr("Entities established for charitable, social, or community-oriented purposes without profit distribution."); },
    icon: "heart",
  },
  {
    id: "government",
    get label() { return tr("Government entity"); },
    get description() { return tr("Official government departments, agencies, or public sector organizations."); },
    icon: "flag",
  },
  {
    id: "educational",
    get label() { return tr("Educational institution"); },
    get description() { return tr("Schools, colleges, universities, or training centers providing formal education or courses."); },
    icon: "scroll",
  },
  {
    id: "student-club",
    get label() { return tr("Student club"); },
    get description() { return tr("Groups formed by students within educational institutions for activities."); },
    icon: "users",
  },
  {
    id: "other",
    get label() { return tr("Other business type"); },
    get description() { return tr("Any business model or organization not covered by the above categories."); },
    icon: "smile",
  },
];

export const CURRENCIES = [
  { code: "USD", get label() { return tr("USD — US Dollar"); } },
  { code: "EUR", get label() { return tr("EUR — Euro"); } },
  { code: "GBP", get label() { return tr("GBP — British Pound"); } },
  { code: "CAD", get label() { return tr("CAD — Canadian Dollar"); } },
  { code: "AUD", get label() { return tr("AUD — Australian Dollar"); } },
];

export const RESTRICTED_COUNTRIES = [
  "Afghanistan",
  "Algeria",
  "Angola",
  "Bolivia",
  "British Virgin Islands",
  "Cameroon",
  "Côte d’Ivoire",
  "Democratic People's Republic of Korea (DPRK) (North Korea)",
  "Democratic Republic of the Congo",
  "Haiti",
  "Iran",
  "Kenya",
  "Laos",
  "Lebanon",
  "Monaco",
  "Myanmar",
  "Namibia",
  "Nepal",
  "Russian Federation",
  "South Sudan",
  "Syria",
  "Trinidad and Tobago",
  "Vanuatu",
  "Venezuela",
  "Vietnam",
  "Yemen",
];

const normalizeCountryText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();

const RESTRICTED_COUNTRY_ALIASES = [
  ...RESTRICTED_COUNTRIES,
  "Cote d'Ivoire",
  "Cote D'Ivoire",
  "Ivory Coast",
  "DPRK",
  "North Korea",
  "Democratic Peoples Republic of Korea",
  "DR Congo",
  "DRC",
  "Congo Kinshasa",
  "Russia",
  "BVI",
].map(normalizeCountryText);

const RESTRICTED_COUNTRY_SET = new Set(RESTRICTED_COUNTRY_ALIASES);

export const isRestrictedCountryName = (value) =>
  RESTRICTED_COUNTRY_SET.has(normalizeCountryText(value));

export const findRestrictedCountryName = (value) => {
  const normalized = ` ${normalizeCountryText(value)} `;
  return RESTRICTED_COUNTRY_ALIASES.find((country) => normalized.includes(` ${country} `)) || "";
};

export const ALL_COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola",
  "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus",
  "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
  "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo", "Costa Rica", "Cote d'Ivoire", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Democratic Republic of the Congo", "Denmark", "Djibouti",
  "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador",
  "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji",
  "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran",
  "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
  "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea",
  "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine",
  "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland",
  "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
  "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Korea", "South Sudan", "Spain",
  "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo",
  "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
  "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City",
  "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe",
];

export const COUNTRIES = ALL_COUNTRIES.filter((country) => !isRestrictedCountryName(country));
